import React, { useMemo, useState } from 'react';
import { motion } from 'motion/react';
import { getTradeBoot, receivedValue, valueTradeNow } from './helpers';
import { TradePartnerLine } from './TradePartner';
import { rowName, dayOf, useSinceMoves, readingNote } from './priceArchive';
import ItemDetailModal from './ItemDetailModal';
import { findSkinImage } from './skinIndex';
import { buildTrades } from './tradeLineage';
// Sales agreed on CSFloat but still inside Steam's trade-protection window.
// Their rows keep no sell price until the money is final, so a received item
// mid-settlement would otherwise be drawn as one you are still holding.
import { useSettling, settlesInLabel } from './settlingSales';


const FILTERS = [
  { key: 'all',      label: 'All trades' },
  { key: 'open',     label: 'Open' },
  { key: 'complete', label: 'Complete' },
];

export default function TradedTab2({
  rows, settings, skinsData = [], livePrices, csfloatPrices, tradePartners,
  lendingEnabled = false, onLendFromTrade,
}) {
  const settlingOf = useSettling();
  const [query, setQuery]   = useState('');
  const [filter, setFilter] = useState('all');
  const [detail, setDetail] = useState(null);   // the traded item whose modal is open

  // Grouped by trade, not by item given — one Steam trade can hand over several
  // items and they all share a tradeId. See buildTrades in tradeLineage.js.
  const trades = useMemo(() => {
    return buildTrades(rows).map((t, idx) => {
      const gaveRows = t.gave.map(e => e.row);
      const received = t.received.map(e => e.row);
      // One valuation for the basket, not a manufactured profit per item —
      // see the note above valueTradeNow in helpers.js.
      const worth = valueTradeNow(gaveRows, received, livePrices);
      return {
        key: t.tradeId || `legacy_${idx}`,
        realIndex: t.gave[0]?.index ?? -1,
        offeredRow: gaveRows[0],
        gave: t.gave, promised: t.promised, at: t.at,
        received, worth,
        cash: getTradeBoot(gaveRows[0]),
        soldCount: worth.sold,
        pnl: worth.potential,
        complete: worth.settled,
      };
    });
  }, [rows, livePrices]);

  // ── Was it a fair trade on the day, and is it still? ──────
  // A swap has no price of its own. The app has only ever been able to say
  // what the received items are worth NOW, against a cost basis inherited from
  // the item given away — which answers a different question from the one
  // anyone asks about a trade.
  //
  // The archive prices both sides on the day the trade happened, so the two
  // real questions become answerable:
  //
  //   then   what the market said each side was worth when you agreed
  //   now    what those same items are worth today, both sides
  //
  // A trade that looked even on the day and is 40% underwater now was a timing
  // call, not a bad valuation, and only the pair of numbers shows that.
  const archivePoints = useMemo(() => {
    const out = [];
    for (const t of trades) {
      const date = dayOf(t.at);
      if (!date) continue;
      t.gave.forEach((g, i) => out.push({ key: `${t.key}|gave|${i}`, name: rowName(g.row), date }));
      t.received.forEach((r, i) => out.push({ key: `${t.key}|got|${i}`, name: rowName(r), date }));
    }
    return out;
  }, [trades]);
  const archive = useSinceMoves(archivePoints, csfloatPrices);

  // Both sides of one trade, valued twice. Returns null unless BOTH sides could
  // be priced — half a valuation is worse than none, because the missing half
  // reads as zero and makes every trade look like a steal.
  const valueTrade = (t) => {
    // Every item on both sides, or nothing — a trade priced on four of its
    // seven given items reads as a steal that never happened.
    const gaveReads = t.gave.map((_, i) => archive.get(`${t.key}|gave|${i}`));
    if (!t.received.length || gaveReads.some(g => !g)) return null;
    const got = t.received.map((_, i) => archive.get(`${t.key}|got|${i}`));
    if (got.some(g => !g)) return null;

    const gaveThen = gaveReads.reduce((s, g) => s + g.then, 0);
    const gaveNow  = gaveReads.reduce((s, g) => s + g.now, 0);
    const gotThen  = got.reduce((s, g) => s + g.then, 0);
    const gotNow   = got.reduce((s, g) => s + g.now, 0);
    return {
      // The reading note comes off the first leg; they all share a date.
      gave: gaveReads[0],
      gaveThen, gaveNow,
      gotThen, gotNow,
      // Cash counts on the received side: it is part of what you took.
      edgeThen: gotThen + t.cash - gaveThen,
      edgeNow:  gotNow  + t.cash - gaveNow,
    };
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return trades.filter(t => {
      if (filter === 'open' && t.complete) return false;
      if (filter === 'complete' && !t.complete) return false;
      if (q) {
        const inOffered = t.gave.some(g => g.row.skin?.value?.toLowerCase().includes(q));
        const inRecv = t.received.some(r => r.skin?.value?.toLowerCase().includes(q));
        if (!inOffered && !inRecv) return false;
      }
      return true;
    });
  }, [trades, query, filter]);

  const totals = useMemo(() => {
    // How the whole trading record looks valued at market, then and now.
    const market = trades.reduce((acc, t) => {
      const v = valueTrade(t);
      if (!v) return acc;
      return {
        n: acc.n + 1,
        then: acc.then + v.edgeThen,
        now: acc.now + v.edgeNow,
        aged: acc.aged + (v.edgeNow >= v.edgeThen ? 1 : 0),
      };
    }, { n: 0, then: 0, now: 0, aged: 0 });

    return {
      pnl:   trades.reduce((s, t) => s + t.pnl, 0),
      cash:  trades.reduce((s, t) => s + t.cash, 0),
      open:  trades.filter(t => !t.complete).length,
      items: trades.reduce((s, t) => s + t.received.length, 0),
      market,
    };
  }, [trades, archive]);   // eslint-disable-line react-hooks/exhaustive-deps -- valueTrade reads archive

  const profC = settings?.profitTextColor || 'var(--green)';
  const lossC = settings?.lossTextColor   || 'var(--red)';
  const pcol  = (v) => v >= 0 ? profC : lossC;

  if (!trades.length) {
    return (
      <div className="tr2-root">
        <div className="lv2-empty">No trades yet — items you mark as Traded appear here as exchange flows.</div>
      </div>
    );
  }

  return (
    <div className="tr2-root">

      {/* ── Headline totals ────────────────────────── */}
      <div className="pt-hero">
        <div className="pt-hero-cell">
          <div className="k">Potential from trading</div>
          <div className="v" style={{ color: pcol(totals.pnl) }}>
            {totals.pnl >= 0 ? '+' : '−'}${Math.abs(totals.pnl).toFixed(2)}
          </div>
          <div className="s">real profit on settled trades, today’s market on the rest</div>
        </div>
        <div className="pt-hero-cell">
          <div className="k">Items received</div>
          <div className="v">{totals.items}</div>
          <div className="s">
            from {trades.length} trade{trades.length !== 1 ? 's' : ''}
            {totals.open > 0 ? ` · ${totals.open} still open` : ''}
          </div>
        </div>
        <div className="pt-hero-cell">
          <div className="k">Cash on the side</div>
          <div className="v">{totals.cash < 0 ? '-' : ''}${Math.abs(totals.cash).toFixed(2)}</div>
          <div className="s">taken in, less what you added</div>
        </div>
        {/* Needs the price archive: nothing in the app records what either side
            of a trade was worth on the day it happened. */}
        {totals.market.n > 0 && (
          <div className="pt-hero-cell">
            <div className="k">Trades at market</div>
            <div className="v" style={{ color: pcol(totals.market.now) }}>
              {totals.market.now >= 0 ? '+' : '−'}${Math.abs(totals.market.now).toFixed(2)}
            </div>
            <div className="s">
              today, against {totals.market.then >= 0 ? '+' : '−'}${Math.abs(totals.market.then).toFixed(2)} on the day
              {' · '}{totals.market.aged} of {totals.market.n} aged well
            </div>
          </div>
        )}
      </div>

      {/* ── Toolbar ────────────────────────────────── */}
      <div className="lv2-bar" style={{ position: 'static', marginBottom: 16 }}>
        <div className="lv2-search">
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="6.5" /><path d="M16 16l4 4" />
          </svg>
          <input placeholder="Search trades…" value={query} onChange={e => setQuery(e.target.value)} />
        </div>
        {FILTERS.map(f => (
          <button key={f.key} className={`lv2-chip ${filter === f.key ? 'on' : ''}`} onClick={() => setFilter(f.key)}>
            {f.label}
          </button>
        ))}
        <span style={{ marginLeft: 'auto', fontSize: '0.92rem', color: 'var(--text-dim)', fontWeight: 600 }}>
          {filtered.length} of {trades.length} trades
        </span>
      </div>

      {/* ── Flows ──────────────────────────────────── */}
      {filtered.length === 0 && <div className="lv2-empty">No trades match your filters.</div>}
      {filtered.map((t, ti) => {
        const dateStr = t.at
          ? new Date(t.at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
          : '—';
        return (
          <motion.div key={t.key} className="tr2-card"
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1], delay: Math.min(ti, 10) * 0.05 }}>

            <div className="tr2-flow">
              {/* Gave */}
              <div className="tr2-side">
                <div className="tr2-side-label">
                  Gave away{t.gave.length > 1 ? ` (${t.gave.length})` : ''}
                </div>
                {/* A list: a trade can hand over several items at once, and
                    drawing only the first is what made a seven-for-thirty-seven
                    look like six trades that received nothing. */}
                <div className="tr2-gave-list">
                  {t.gave.map(g => {
                    const gImg = findSkinImage(skinsData, g.row.skin?.value, g.row.condition?.value);
                    return (
                      <div key={g.index} className="tr2-offered idm-clickable"
                        role="button" tabIndex={0} title="Open details"
                        onClick={() => setDetail({ row: g.row, img: gImg })}
                        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setDetail({ row: g.row, img: gImg }); } }}>
                        {gImg && <img src={gImg} alt="" />}
                        <div style={{ minWidth: 0 }}>
                          <div className="tr2-name">{g.row.skin?.value}</div>
                          <div className="tr2-sub">
                            {g.row.condition?.value || '—'} · cost ${(parseFloat(g.row.buyPrice) || 0).toFixed(2)}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Hub */}
              <div className="tr2-hub">
                <div className="arrows">⇢⇢⇢</div>
                {t.cash !== 0 && (
                  <div className="cash">
                    {t.cash > 0 ? `+$${t.cash.toFixed(2)} cash` : `-$${Math.abs(t.cash).toFixed(2)} cash`}
                  </div>
                )}
                <div className="date">{dateStr}</div>
                <TradePartnerLine row={t.offeredRow} index={tradePartners} style={{ textAlign: 'center' }} />
              </div>

              {/* Received */}
              <div className="tr2-side">
                <div className="tr2-side-label" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  Received ({t.received.length})
                  <span className="tr2-status" style={{
                    background: t.complete ? 'rgba(34,197,94,0.14)' : 'rgba(245,158,11,0.14)',
                    color: t.complete ? profC : 'var(--amber)',
                  }}>
                    {t.complete ? 'Complete' : t.received.length === 0 ? 'Nothing logged' : `${t.soldCount}/${t.received.length} sold`}
                  </span>
                </div>
                <div className="tr2-received">
                  {t.received.length === 0 && (
                    <div style={{ fontSize: '0.92rem', color: 'var(--text-dim)', padding: '8px 2px' }}>
                      No received items recorded for this trade.
                      {/* A trade where nothing came back is the ambiguous one: a
                          lend, a gift or an off-platform sale all look like this
                          to Steam. Only the person who made it knows which, so
                          the answer is offered rather than guessed. */}
                      {lendingEnabled && onLendFromTrade && t.realIndex !== -1 && (
                        <div style={{ marginTop: 10 }}>
                          <button type="button" className="tr2-lend-btn"
                            onClick={() => onLendFromTrade(t.offeredRow, t.realIndex)}>
                            This was a lend
                          </button>
                          <div style={{ marginTop: 6, fontSize: '0.9rem', lineHeight: 1.5 }}>
                            Moves it to Lending and keeps it in your holdings, with{' '}
                            {tradePartners?.forRow?.(t.offeredRow)?.name || 'the person Steam recorded'} as the borrower.
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  {t.received.map((r, i) => {
                    const img = findSkinImage(skinsData, r.skin?.value, r.condition?.value);
                    const v   = receivedValue(r, livePrices);
                    const sold = v.sold;
                    // Sold on CSFloat but still inside Steam's trade-protection
                    // window. The row keeps no sell price until the money is
                    // final, so without this it reads as "holding" — an item you
                    // have already agreed to part with.
                    const settling = !sold ? settlingOf(r) : null;
                    return (
                      <div key={i} className={`tr2-recv-item idm-clickable ${sold ? 'sold' : ''}${settling ? ' settling' : ''}`}
                        role="button" tabIndex={0} title="Open details"
                        onClick={() => setDetail({ row: r, img })}
                        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setDetail({ row: r, img }); } }}>
                        {img && <img src={img} alt="" />}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div className="tr2-name">{r.skin?.value}</div>
                          <div className="tr2-sub">
                            {r.condition?.value || '—'} · {sold
                              ? 'sold'
                              : settling
                                ? `sold · ${settlesInLabel(settling.settlesAt) || 'settling'}`
                                : 'holding'}
                          </div>
                        </div>
                        {/* The price, not a verdict. Neutral colour on purpose:
                            an item's inherited cost slice is not a price anyone
                            paid, so there is no honest win/lose to paint here. */}
                        <div style={{ flexShrink: 0, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                          <div style={{ fontSize: '0.94rem', fontWeight: 700 }}>
                            {/* The agreed price, not today's market: that is the
                                number that will land when protection ends. */}
                            {settling
                              ? `$${Number(settling.price).toFixed(2)}`
                              : v.value != null ? `$${v.value.toFixed(2)}` : 'n/a'}
                          </div>
                          <div style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-dim)' }}>
                            {sold ? 'sold for' : settling ? 'on CSFloat' : v.value != null ? 'worth now' : 'no price'}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Summary strip */}
            <div className="tr2-summary">
              <div className="tr2-sum">
                <div className="k">Gave away</div>
                <div className="v">${t.worth.gave.toFixed(2)}</div>
              </div>
              <div className="tr2-sum">
                <div className="k">{t.worth.settled ? 'Came back' : 'Worth today'}</div>
                <div className="v">
                  ${t.worth.back.toFixed(2)}
                  {t.worth.unpriced > 0 && (
                    <span style={{ display: 'block', fontSize: '0.87rem', fontWeight: 600, color: '#f87171' }}>
                      {t.worth.unpriced} item{t.worth.unpriced === 1 ? '' : 's'} unpriced
                    </span>
                  )}
                </div>
              </div>
              {t.cash !== 0 && (
                <div className="tr2-sum">
                  <div className="k">{t.cash > 0 ? 'Cash received' : 'Cash paid'}</div>
                  <div className="v" style={{ color: t.cash > 0 ? profC : '#f87171' }}>
                    {t.cash > 0 ? `+$${t.cash.toFixed(2)}` : `-$${Math.abs(t.cash).toFixed(2)}`}
                  </div>
                </div>
              )}
              {(() => {
                const v = valueTrade(t);
                if (!v) return null;
                return (
                  <>
                    <div className="tr2-sum" title={readingNote(v.gave)}>
                      <div className="k">Value on the day</div>
                      <div className="v" style={{ color: pcol(v.edgeThen) }}>
                        {v.edgeThen >= 0 ? '+' : '−'}${Math.abs(v.edgeThen).toFixed(2)}
                        <span style={{ display: 'block', fontSize: '0.87rem', fontWeight: 600, color: 'var(--text-dim)' }}>
                          gave ${v.gaveThen.toFixed(2)} · got ${v.gotThen.toFixed(2)}
                        </span>
                      </div>
                    </div>
                    <div className="tr2-sum">
                      <div className="k">Same trade today</div>
                      <div className="v" style={{ color: pcol(v.edgeNow) }}>
                        {v.edgeNow >= 0 ? '+' : '−'}${Math.abs(v.edgeNow).toFixed(2)}
                        <span style={{ display: 'block', fontSize: '0.87rem', fontWeight: 600, color: 'var(--text-dim)' }}>
                          gave ${v.gaveNow.toFixed(2)} · got ${v.gotNow.toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </>
                );
              })()}
              <div className="tr2-sum" style={{ marginLeft: 'auto' }}>
                <div className="k">{t.worth.settled ? 'Profit from this trade' : 'Potential profit'}</div>
                <div className="v" style={{ color: pcol(t.worth.potential) }}>
                  {t.worth.potential >= 0 ? '+' : '−'}${Math.abs(t.worth.potential).toFixed(2)}
                  <span style={{ display: 'block', fontSize: '0.87rem', fontWeight: 600, color: 'var(--text-dim)' }}>
                    {t.worth.settled
                      ? 'every item sold · real money'
                      : t.worth.sold > 0
                        ? `${t.worth.sold} sold, ${t.worth.held} held at market`
                        : 'nothing sold yet · at today’s prices'}
                  </span>
                </div>
              </div>
            </div>
          </motion.div>
        );
      })}

      {detail && (
        <ItemDetailModal
          variant="classic"
          kind={parseFloat(detail.row.sellPrice) > 0 ? 'sold' : 'item'}
          name={detail.row.skin?.value}
          condition={detail.row.condition?.value}
          image={detail.img}
          history={detail.row.priceHistory}
          livePrices={livePrices}
          csfloatPrices={csfloatPrices}
          row={detail.row}
          rows={rows || []}
          /* the trade overview draws a thumbnail for every item on both sides */
          skinsData={skinsData}
          /* click-through: any item in the trade chain re-centres this modal */
          onOpenRow={r => setDetail({ row: r, img: findSkinImage(skinsData, r.skin?.value, r.condition?.value) })}
          onClose={() => setDetail(null)}
        />
      )}
    </div>
  );
}
